// FileTree.js
import React, { useState } from "react";
import { ChevronDown, ChevronRight, File, Folder, StickyNote } from "lucide-react";
import { normalizeFilePath, hasFileSummaryKey } from "./utils";
import "./FileTree.css"; // Make sure to import the CSS file

const LoadingDots = () => (
  <div className="loading-dots">
    <div className="loading-dot"></div>
    <div className="loading-dot"></div>
    <div className="loading-dot"></div>
  </div>
);

export const FileTree = ({
  structure,
  fileSummaries,
  onFileClick,
  className = "",
  processingFiles = [], // New prop to track which files are being processed
  notes = [], // Array of notes with filePath property
}) => {
  const [expandedFolders, setExpandedFolders] = useState(
    new Set(["backend", "frontend"])
  );

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const hasFileSummary = (path) => {
    return hasFileSummaryKey(fileSummaries, path);
  };

  const hasNote = (path) => {
    if (!notes || notes.length === 0) return false;
    const normalizedPath = normalizeFilePath(path);
    return notes.some(note => note.filePath && normalizeFilePath(note.filePath) === normalizedPath);
  };

  const getNoteCount = (path) => {
    if (!notes || notes.length === 0) return 0;
    const normalizedPath = normalizeFilePath(path);
    return notes.filter(note => note.filePath && normalizeFilePath(note.filePath) === normalizedPath).length;
  };

  const isProcessing = (path) => {
    return processingFiles.includes(normalizeFilePath(path));
  };

  /** Paths GitHub reported as files (not folders). Dots in the name must not imply "file" (e.g. `.github` is a folder). */
  const buildFileTree = (items, filePathSet) => {
    const tree = {};

    items.forEach((item) => {
      if (!item?.path) return;
      const parts = item.path.split("/");
      let current = tree;

      parts.forEach((part, index) => {
        const pathSoFar = parts.slice(0, index + 1).join("/");
        const isLeafForThisItem = index === parts.length - 1;
        const isDeclaredFile = isLeafForThisItem && filePathSet.has(item.path);
        if (!current[part]) {
          current[part] = {
            isFile: isDeclaredFile,
            path: pathSoFar,
            children: {},
          };
        } else if (isDeclaredFile) {
          current[part].isFile = true;
        }
        current = current[part].children;
      });
    });

    return tree;
  };

  const ensureFoldersExpandable = (nodeDict) => {
    for (const node of Object.values(nodeDict)) {
      if (Object.keys(node.children).length > 0) {
        node.isFile = false;
        ensureFoldersExpandable(node.children);
      }
    }
  };

  const renderNode = (node, name, level = 0) => {
    const hasChildren = Object.keys(node.children).length > 0;
    const isExpanded = expandedFolders.has(node.path);
    const processing = isProcessing(node.path);

    return (
      <div key={node.path} className="file-tree">
        <div
          className="file-tree-item"
          style={{ paddingLeft: `${level * 16}px` }}
          onClick={() => {
            if (node.isFile) {
              onFileClick(node.path);
            } else if (hasChildren) {
              toggleFolder(node.path);
            }
          }}
        >
          {!node.isFile &&
            hasChildren &&
            (isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            ))}

          {node.isFile ? (
            <>
              <File size={16} className="file-icon" />
              <span
                className={`file-name ${
                  hasFileSummary(node.path) ? "has-summary" : ""
                }`}
              >
                {name}
              </span>
              {hasNote(node.path) && (
                <span className="file-note-indicator" title={`${getNoteCount(node.path)} note(s)`}>
                  <StickyNote size={14} />
                  {getNoteCount(node.path) > 1 && (
                    <span className="note-count-badge">{getNoteCount(node.path)}</span>
                  )}
                </span>
              )}
              {processing && <LoadingDots />}
            </>
          ) : (
            <>
              <Folder size={16} className="folder-icon" />
              <span className="file-name">{name}</span>
            </>
          )}
        </div>

        {!node.isFile && isExpanded && hasChildren && (
          <div>
            {Object.entries(node.children).map(([childName, childNode]) =>
              renderNode(childNode, childName, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const filePathSet = new Set(
    (structure.files || []).map((f) => f.path).filter(Boolean)
  );
  const allItems = [...(structure.folders || []), ...(structure.files || [])];
  const tree = buildFileTree(allItems, filePathSet);
  ensureFoldersExpandable(tree);

  return (
    <div className={className}>
      {Object.entries(tree).map(([name, node]) => renderNode(node, name))}
    </div>
  );
};
